import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Profile } from '@shared/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function useProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user?.id || '')
        .single();

      if (error) throw error;

      if (!data) {
        throw new Error('Profile not found');
      }

      return data;
    },
    enabled: !!user?.id,
  });
}

// Tracks the most recent blob URL minted per avatar path so a refetch (e.g.
// after an upload invalidates the cache) can revoke the URL it is replacing.
// Only the latest URL per path is ever held here, and the React Query cache
// likewise only ever holds the latest value for a given key — so we revoke a
// URL strictly before it is overwritten, never one an <img> is still showing.
const avatarObjectUrls = new Map<string, string>();

export function useAvatarUrl(avatarPath: string | null | undefined) {
  return useQuery({
    queryKey: ['avatar-url', avatarPath],
    queryFn: async () => {
      if (!avatarPath) return null;

      // Download the file to get a blob URL that's cached by React Query
      const { data, error } = await supabase.storage
        .from('images')
        .download(avatarPath);

      if (error) throw error;
      if (!data) return null;

      // Revoke the prior blob URL for this path before minting a replacement.
      // The cache only retains the newest URL per key, so the one we revoke is
      // already being discarded — this frees the leaked blob without any
      // use-after-revoke.
      const previousUrl = avatarObjectUrls.get(avatarPath);
      if (previousUrl) {
        URL.revokeObjectURL(previousUrl);
      }

      // Create a blob URL from the downloaded data
      const objectUrl = URL.createObjectURL(data);
      avatarObjectUrls.set(avatarPath, objectUrl);
      return objectUrl;
    },
    enabled: !!avatarPath,
    staleTime: 1000 * 60 * 60 * 24, // Cache for 24 hours
    gcTime: 1000 * 60 * 60 * 24 * 7, // Keep in cache for 7 days
  });
}

export function useUpdateProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profile: Partial<Profile>) => {
      const { data, error } = await supabase
        .from('profiles')
        .update({
          ...(profile.full_name && { full_name: profile.full_name }),
          ...(profile.avatar_path && { avatar_path: profile.avatar_path }),
          ...(profile.notifications_enabled !== undefined && {
            notifications_enabled: profile.notifications_enabled,
          }),
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user?.id || '')
        .select()
        .single();

      if (error) throw error;

      return data;
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(['profile', user?.id], data);
      }
    },
  });
}

export function useUploadAvatar() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      if (!user) throw new Error('User not authenticated');

      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        throw new Error(
          'Invalid file type. Please upload a JPEG, PNG, or WebP image.',
        );
      }

      if (file.size > 5 * 1024 * 1024) {
        throw new Error(
          'File too large. Please upload an image smaller than 5MB.',
        );
      }

      const filePath = `${user.id}/profile`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file, {
          upsert: true,
          contentType: file.type,
        });

      if (uploadError) throw uploadError;

      const { data, error: updateError } = await supabase
        .from('profiles')
        .update({
          avatar_path: filePath,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (updateError) throw updateError;

      return data;
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(['profile', user?.id], data);
        queryClient.invalidateQueries({
          queryKey: ['avatar-url', data.avatar_path],
        });
      }
    },
  });
}
